import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { useStaticContext } from '../../context/contexts/static';
import { useOfferMoreGigsContext } from '../../components/OfferMoreGigs/context';
import { SECTION_NAME } from '../../components/OfferMoreGigs/constants';
import { useShouldOfferMoreGigs } from '..';

/**
 * lazy load the offerMoreGigs component and fetch more gigs data
 * @param isOpen - whether the modal/popover is opened
 */
export const useLoadOfferMoreGigs = (isOpen) => {
    const { shouldShowMoreGigsV3, shouldShowMoreGigsV4 } = useShouldOfferMoreGigs();
    const shouldOfferMoreGigs = shouldShowMoreGigsV3 || shouldShowMoreGigsV4;

    const { getMoreGigsData } = useStaticContext();
    const { updateGigsData, gigs, offerMoreGigsComponentRef, loadingOfferMoreGigs, setLoadingOfferMoreGigs } = useOfferMoreGigsContext();

    const loadOfferMoreGigs = async() => {
        try {
            setLoadingOfferMoreGigs(true);

            const [offerMoreGigsComponent, gigsData] = await Promise.all([
                lazyLoadOfferMoreGigsComponent(),
                fetchMoreGigsData()
            ]);

            offerMoreGigsComponentRef.current = offerMoreGigsComponent?.default;

            if (!isEmpty(gigsData)) {
                updateGigsData(gigsData);
            }
        } catch (error) {
            reportLoadError(error);
        } finally {
            setLoadingOfferMoreGigs(false);
        }
    };

    useEffect(() => {
        if (isOpen && shouldOfferMoreGigs && isEmpty(gigs) && !loadingOfferMoreGigs) {
            loadOfferMoreGigs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const lazyLoadOfferMoreGigsComponent = async() => {
        try {
            const offerMoreGigsComponentLoader = import(/* webpackChunkName: 'OfferMoreGigs' */'../../components/OfferMoreGigs');

            return await offerMoreGigsComponentLoader;
        } catch (error) {
            reportLazyLoadError(error);
            throw error;
        }
    };

    const fetchMoreGigsData = async() => await getMoreGigsData(SECTION_NAME);

    const reportLazyLoadError = (error) => {
        logger.warn(error, {
            description: 'Failed to lazy load OfferMoreGigs component'
        });
    };

    const reportLoadError = (error) => {
        logger.warn(error, {
            description: 'Failed to load OfferMoreGigs'
        });
    };
};
